import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

import { coreApi } from "@/core/services/coreApi";
import { sync } from "@/core/sync";
import { CORE_EVENTS, makeEvent } from "@/core/sync/events";

/**
 * settingsStore — core, plugin-agnostic settings.
 * Backend-synced (PUT /api/settings) + cross-window broadcast (SETTINGS_UPDATED).
 */

const defaultSettings = {
  defaultMode: "manual",
  defaultAutoInterval: 5,
  muted: false,
};

let saveTimer = null;
let pendingPatch = {};

const scheduleSave = () => {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(async () => {
    const patch = pendingPatch;
    pendingPatch = {};
    saveTimer = null;
    if (!patch || Object.keys(patch).length === 0) return;
    try {
      await coreApi.putSettings(patch);
    } catch (e) {
      console.warn(
        "[settingsStore] save failed",
        e?.userMessage || e?.message || e,
      );
    }
  }, 500);
};

const mergePatch = (patch) => {
  pendingPatch = { ...pendingPatch, ...patch };
};

const broadcast = (patch) => {
  sync.publish(makeEvent(CORE_EVENTS.SETTINGS_UPDATED, patch, "core"));
};

export const useSettingsStore = create(
  persist(
    (set, get) => ({
      settings: defaultSettings,
      hydrated: false,

      setSettings: (patch, opts = {}) => {
        set((state) => ({ settings: { ...state.settings, ...patch } }));
        if (!opts.silent) {
          broadcast(patch);
          mergePatch(patch);
          scheduleSave();
        }
      },

      toggleMute: () => {
        const next = !get().settings.muted;
        get().setSettings({ muted: next });
        // The mute event is a hot path — also fire the dedicated event
        // so the audio service reacts before the debounced save.
        sync.publish(
          makeEvent(CORE_EVENTS.MUTE_TOGGLED, { muted: next }, "core"),
        );
      },

      hydrateFromBackend: async () => {
        try {
          const doc = await coreApi.getSettings();
          if (!doc) return;
          set((state) => ({
            settings: {
              ...state.settings,
              defaultMode: doc.defaultMode ?? state.settings.defaultMode,
              defaultAutoInterval:
                doc.defaultAutoInterval ?? state.settings.defaultAutoInterval,
              muted: doc.muted ?? state.settings.muted,
            },
            hydrated: true,
          }));
        } catch {
          set({ hydrated: true });
        }
      },
    }),
    {
      name: "ic-bingo-settings",
      storage: createJSONStorage(() => localStorage),
      version: 2,
      partialize: (state) => ({ settings: state.settings }),
    },
  ),
);
